import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, KeyRound, UserCheck } from "lucide-react";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addingOwner, setAddingOwner] = useState(null); // business obj

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/admin/clients"), api.get("/admin/users")]);
    setClients(c.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  // For each business id, find its owner login (user whose id == business.id and role == client)
  const ownerOf = (clientId) => users.find((u) => u.id === clientId && u.role === "client");

  const remove = async (id) => {
    if (!window.confirm("Delete this client? Assigned users will be unassigned.")) return;
    try { await api.delete(`/admin/clients/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ Admin / Clients ]</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Clients & Businesses</h1>
            <p className="text-sm text-stone-500 mt-1 font-display-italic">Each business can have one owner login that sees its assigned employees.</p>
          </div>
          <Button data-testid="add-client-button" onClick={() => setAdding(true)} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90 text-white tracking-wide">
            <Plus className="h-4 w-4 mr-2" /> Add client
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
          {clients.map((c) => {
            const assigned = users.filter((u) => u.client_id === c.id && u.role === "employee").length;
            const owner = ownerOf(c.id);
            return (
              <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-white p-5 hover:bg-stone-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1">Business</div>
                    <div className="font-display text-xl font-bold truncate">{c.name}</div>
                    <div className="text-xs font-mono-num text-stone-500 mt-1 truncate">{c.contact_email || "—"}</div>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-stone-100" title="Edit business">
                      <Edit3 className="h-4 w-4 text-stone-700" />
                    </button>
                    <button onClick={() => remove(c.id)} className="p-1.5 hover:bg-[#a8421f]/10" title="Delete business">
                      <Trash2 className="h-4 w-4 text-[#a8421f]" />
                    </button>
                  </div>
                </div>

                {/* Owner login row */}
                <div className="mt-4 pt-4 border-t border-stone-100">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-2">Owner Login</div>
                  {owner ? (
                    <div className="flex items-center gap-2 text-xs">
                      <UserCheck className="h-3.5 w-3.5 text-[#1f4d2e]" />
                      <div className="min-w-0 flex-1">
                        <div className="font-bold truncate">{owner.name}</div>
                        <div className="font-mono-num text-stone-500 truncate">{owner.email}</div>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid={`add-owner-${c.id}`}
                      onClick={() => setAddingOwner(c)}
                      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-[#7a5e0f] bg-[#d4a82a]/15 border border-[#d4a82a]/40 rounded px-2 py-1 hover:bg-[#d4a82a]/25"
                    >
                      <KeyRound className="h-3 w-3" /> Add owner login
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="tracking-[0.15em] uppercase text-stone-500 font-bold">Employees</span>
                  <span className="font-mono-num font-bold text-lg">{assigned}</span>
                </div>
              </div>
            );
          })}
          {clients.length === 0 && (
            <div className="bg-white col-span-full text-center py-16 text-sm text-stone-500">No clients yet — add your first business.</div>
          )}
        </div>
      </div>

      <ClientDialog open={adding} onClose={() => setAdding(false)} onSaved={load} />
      <ClientDialog open={!!editing} onClose={() => setEditing(null)} initial={editing} onSaved={load} />
      <OwnerLoginDialog business={addingOwner} onClose={() => setAddingOwner(null)} onSaved={load} />
    </AppShell>
  );
}

function ClientDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState({ name: "", contact_email: "" });
  useEffect(() => {
    if (initial) setForm({ name: initial.name, contact_email: initial.contact_email || "" });
    else setForm({ name: "", contact_email: "" });
  }, [initial, open]);

  const save = async (e) => {
    e?.preventDefault();
    try {
      const payload = { name: form.name, contact_email: form.contact_email || null };
      if (initial) await api.put(`/admin/clients/${initial.id}`, payload);
      else await api.post("/admin/clients", payload);
      toast.success(initial ? "Updated" : "Created");
      onClose();
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">{initial ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Business Name</Label>
            <Input className="rounded-md mt-1" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Contact Email</Label>
            <Input className="rounded-md mt-1" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90">{initial ? "Save" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function OwnerLoginDialog({ business, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (business) setForm({ name: "", email: business.contact_email || "", password: "" });
  }, [business]);
  if (!business) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/clients/${business.id}/owner`, form);
      toast.success(`Owner login created for ${business.name}`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!business} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Add Owner Login</DialogTitle>
          <div className="text-xs text-stone-500 mt-1">
            For business: <span className="font-bold text-stone-800">{business.name}</span>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Owner Name</Label>
            <Input data-testid="owner-name-input" className="rounded-md mt-1" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Maria Santos" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Email</Label>
            <Input data-testid="owner-email-input" className="rounded-md mt-1" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Password</Label>
            <Input data-testid="owner-password-input" className="rounded-md mt-1 font-mono-num" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" />
            <div className="text-[11px] text-stone-500 mt-1 font-display-italic">Share these credentials with the client privately.</div>
          </div>
          <DialogFooter>
            <Button data-testid="owner-create-submit" type="submit" disabled={saving} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90">
              {saving ? "Creating…" : "Create Owner Login"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
